import { useMutation } from '@tanstack/react-query';
import type { ImportReport, WorkflowModel } from '@couch/shared';
import { Link } from 'react-router';
import { api, errorMessage } from '../../api/client';

const fileOf = (url: string) => url.split('/').pop()?.split('?')[0] ?? url;

/** Starts one model download on the machine, into the folder the template names (doc 36). */
function ModelDownload({ model, machineId }: { model: WorkflowModel; machineId: string }) {
  const start = useMutation({ mutationFn: () => api.startDownload(machineId, { url: model.url ?? '', ...(model.directory ? { folder: model.directory } : {}) }) });
  if (!model.url) return null;
  if (start.isSuccess) return <> · <Link to={`/machines/${machineId}`}>downloading — see the machine page</Link></>;
  return (
    <>
      {' '}<button type="button" className="button-quiet" disabled={start.isPending} onClick={() => { start.mutate(); }}>{start.isPending ? 'Starting…' : 'Download'}</button>
      {start.isError && <span role="alert" className="form-error"> {errorMessage(start.error)}</span>}
    </>
  );
}

/** What an imported workflow still needs on the machine: custom nodes, model files, fixes (doc 35). */
export function ImportReportView({ report, machineId }: { report: ImportReport; machineId: string }) {
  const missingModels = report.models.filter((m) => !m.installed);
  const otherErrors = report.validation.errors.filter((e) => e.code !== 'not_an_option' && e.code !== 'unknown_node');
  const ready = report.missingNodes.length === 0 && missingModels.length === 0 && otherErrors.length === 0;
  return (
    <div className="import-report" aria-live="polite">
      {ready && <p className="notice notice-success">Everything this workflow needs is on the machine.</p>}
      {report.missingNodes.length > 0 && (
        <div>
          <h3>Custom nodes to install</h3>
          <p className="muted">{report.missingNodes.join(', ')}</p>
          <p><Link to={`/machines/${machineId}#nodepacks-title`}>Find and install them on the machine page</Link> — or ask Claude to.</p>
        </div>
      )}
      {missingModels.length > 0 && (
        <div>
          <h3>Model files to download</h3>
          <ul className="import-models">
            {missingModels.map((m) => (
              <li key={m.name}>
                <code>{m.name}</code>{m.directory && <span className="muted"> → {m.directory}</span>}
                {m.url && <> · <a href={m.url} target="_blank" rel="noreferrer noopener">{fileOf(m.url) === m.name ? 'link' : fileOf(m.url)}</a></>}
                <ModelDownload model={m} machineId={machineId} />
              </li>
            ))}
          </ul>
          <p className="muted">Download them here, or ask Claude to; progress shows on <Link to={`/machines/${machineId}`}>the machine page</Link>. Save the workflow meanwhile — it runs once the files are there.</p>
        </div>
      )}
      {otherErrors.length > 0 && (
        <div>
          <h3>Still to fix ({otherErrors.length})</h3>
          <ul>{otherErrors.slice(0, 8).map((e, i) => <li key={i}><code>{e.classType}</code> {e.message}</li>)}</ul>
        </div>
      )}
      {report.warnings.length > 0 && <ul className="muted">{report.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
    </div>
  );
}
